import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Breadcrumbs,
  Link as MuiLink,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Divider,
  Paper,
  Skeleton,
  Avatar,
  Grid,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
  Star as StarIcon,
  Event as EventIcon,
  BarChart as StatsIcon,
  Search as SeoIcon,
  Hotel as HotelIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import {
  contentApi,
  type CityPage,
  type ContentStatus,
} from '../../services/api-client';
import { useAuth } from '../../contexts/GoogleAuthContext';
import ContentStatusChip from '../../components/content/ContentStatusChip';

// =============================================================================
// Tab panel helper
// =============================================================================

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// =============================================================================
// CityEditor — tabbed city guide editor (read-only for now, writes in future)
// =============================================================================

export default function CityEditor() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [tabIndex, setTabIndex] = useState(0);

  const canEdit =
    user?.role === 'admin' || user?.effective_permissions?.content?.edit === true;

  // Fetch city
  const {
    data: city,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['content', 'city', slug],
    queryFn: () => contentApi.getCity(slug!),
    enabled: !!slug,
    staleTime: 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="text" width={500} height={24} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  if (fetchError || !city) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load city guide. It may not exist or the Content API may be unreachable.
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/content/cms/cities')}>
          Back to Cities
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to cities">
            <IconButton size="small" onClick={() => navigate('/content/cms/cities')}>
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Breadcrumbs separator="›" sx={{ fontSize: '0.875rem' }}>
            <MuiLink
              component="button"
              variant="body2"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate('/content/cms/cities')}
            >
              Cities
            </MuiLink>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
              {city.name}
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContentStatusChip status={city.status || 'published'} />

          <Tooltip title="View locations">
            <Button
              size="small"
              variant="outlined"
              startIcon={<HotelIcon />}
              onClick={() => navigate(`/content/cms/cities/${slug}/locations`)}
            >
              Locations
            </Button>
          </Tooltip>

          <Tooltip title="View on site">
            <IconButton
              size="small"
              component="a"
              href={`https://curatedby.tripsby.ai/cities/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Hero section */}
      {city.heroImages?.[0] && (
        <Box
          sx={{
            position: 'relative',
            height: 200,
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={city.heroImages[0]}
            alt={city.name}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
              color: 'white',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {city.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {city.subtitle}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Tabs */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<InfoIcon />} iconPosition="start" label="Overview" />
          <Tab icon={<StarIcon />} iconPosition="start" label="Highlights" />
          <Tab icon={<StatsIcon />} iconPosition="start" label="Stats & Rankings" />
          <Tab icon={<EventIcon />} iconPosition="start" label="Events" />
          <Tab icon={<SeoIcon />} iconPosition="start" label="About" />
        </Tabs>
      </Paper>

      {/* Tab: Overview */}
      <TabPanel value={tabIndex} index={0}>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Basic Information
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="City Name"
                  value={city.name}
                  disabled
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Subtitle"
                  value={city.subtitle}
                  disabled
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Slug"
                  value={city.slug}
                  disabled
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    size="small"
                    label="Latitude"
                    value={city.geoCode?.lat ?? ''}
                    disabled
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Longitude"
                    value={city.geoCode?.long ?? ''}
                    disabled
                    sx={{ flex: 1 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ width: { xs: '100%', md: 320 } }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Hero Images ({city.heroImages?.length || 0})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {city.heroImages?.map((img: string, i: number) => (
                    <Box
                      key={i}
                      component="img"
                      src={img}
                      alt={`Hero ${i + 1}`}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Metadata
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Generated by: {city.generatedBy || 'unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Updated: {city.updatedAt ? new Date(city.updatedAt).toLocaleDateString() : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Published: {city.publishedAt ? new Date(city.publishedAt).toLocaleDateString() : '-'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </TabPanel>

      {/* Tab: Highlights */}
      <TabPanel value={tabIndex} index={1}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {city.highlights?.map((h: any, i: number) => (
            <Card key={i} variant="outlined">
              <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                {h.image && (
                  <Box
                    component="img"
                    src={h.image}
                    alt={h.title}
                    sx={{
                      width: 120,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {h.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {h.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
          {(!city.highlights || city.highlights.length === 0) && (
            <Typography color="text.secondary">No highlights defined.</Typography>
          )}
        </Box>
      </TabPanel>

      {/* Tab: Stats & Rankings */}
      <TabPanel value={tabIndex} index={2}>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                City Stats
              </Typography>
              {city.stats && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {city.stats.airports && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Airports</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {city.stats.airports.map((a: string) => (
                          <Chip key={a} label={a} size="small" />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {Object.entries(city.stats)
                    .filter(([k]) => k !== 'airports')
                    .map(([key, value]) => (
                      <Box key={key}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {key}
                        </Typography>
                        <Typography variant="body2">{String(value)}</Typography>
                      </Box>
                    ))}
                </Box>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Rankings
              </Typography>
              {city.rankings && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {Object.entries(city.rankings).map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ textTransform: 'capitalize', minWidth: 100 }}
                      >
                        {key}
                      </Typography>
                      <Box
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.100',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.min(100, (Number(value) / 100) * 100)}%`,
                            height: '100%',
                            bgcolor: Number(value) <= 10 ? 'success.main' : Number(value) <= 30 ? 'info.main' : 'warning.main',
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 30 }}>
                        #{value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* Tab: Events */}
      <TabPanel value={tabIndex} index={3}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {city.events?.map((ev: any, i: number) => (
            <Card key={i} variant="outlined">
              <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                {ev.image && (
                  <Box
                    component="img"
                    src={ev.image}
                    alt={ev.name}
                    sx={{
                      width: 120,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {ev.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5 }}>
                    <Chip label={ev.date} size="small" icon={<EventIcon />} />
                    {ev.location && <Chip label={ev.location} size="small" variant="outlined" />}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {ev.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
          {(!city.events || city.events.length === 0) && (
            <Typography color="text.secondary">No events defined.</Typography>
          )}
        </Box>
      </TabPanel>

      {/* Tab: About */}
      <TabPanel value={tabIndex} index={4}>
        {city.about && (
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {city.about.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
                  >
                    {city.about.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            {city.about.image && (
              <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
                <Box
                  component="img"
                  src={city.about.image}
                  alt={city.about.title}
                  sx={{
                    width: '100%',
                    height: 220,
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </TabPanel>
    </Box>
  );
}
